import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { MouseEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductNotesCard } from "./ProductNotesCard";
import { ProductFilesSection } from "./ProductFilesSection";
import {
  Loader2, AlertCircle, Package, Users, FolderOpen,
  MapPin, ChevronRight, Search, Star
} from "lucide-react";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import type { SystemProductNote, ChangeRecord, ProductFile } from "@/types/productNotes";
import { getLenderLogo } from "@/lib/LenderLogos";
import { getBankLogoUrl, type BankLogoMap } from "@/lib/bankLogos";
import {
  type LocationCard as LocationCardData,
  getAllLocations,
  getLoanTypesForLocation,
  getLendersForLocationLoanType,
} from "@/lib/bankLoanTypeLocations";
import {
  subscribeToSystemNotes,
  subscribeToSystemNotesAnyCity,
  updateSystemNotes,
} from "@/services/productNotesService";
import {
  ALL_CITIES,
  matchOption,
  matchLoanTypeOption,
  type ProductNotesTab,
} from "@/lib/askbotDeeplinks";

const LOAN_TYPE_ICONS: Record<string, string> = {
  "Home Loan": "🏠",
  "Loan Against Property (LAP)": "🏢",
  "Personal Loan": "💳",
  "Business Loan": "💼",
  "Car Loan": "🚗",
  "Education Loan": "🎓",
  "Gold Loan": "🥇",
  "Credit Card": "💴",
  "Overdraft": "🏦",
  "Working Capital": "⚙️",
};

// ─────────────────────────── LenderLogo component ────────────────────────────
// Prefers the admin-curated logo from appConfig/main (same source PayoutsPage
// uses) and falls back to the bundled local logo map, then to an initial avatar.
function LenderLogo({ lender, loanType, size = "sm", bankLogoUrls }: { lender: string; loanType: string; size?: "sm" | "lg"; bankLogoUrls?: BankLogoMap }) {
  const chipH   = size === "lg" ? "h-14 w-14" : "h-12 w-12";
  const avatarH = size === "lg" ? "h-10 w-10" : "h-9 w-9";

  const logo = getBankLogoUrl(bankLogoUrls ?? {}, lender) ?? getLenderLogo(lender, loanType);
  const [failed, setFailed] = useState(false);

  useEffect(() => { setFailed(false); }, [lender, loanType, logo]);

  if (logo && !failed) {
    return (
      <div className={`${chipH} rounded-xl bg-white border border-border/60 shadow-sm flex items-center justify-center shrink-0 p-0.5 overflow-hidden`}>
        <img
          src={logo}
          alt={lender}
          className="h-full w-full object-contain"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }
  return (
    <div className={`${avatarH} rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-green-700 dark:text-green-300 text-sm font-bold shrink-0`}>
      {lender.charAt(0).toUpperCase()}
    </div>
  );
}

// ─────────────────────────── SearchInput ─────────────────────────────────────
function SearchInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative mb-3">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-green-400 transition-all"
      />
    </div>
  );
}

// ─────────────────────────── Step label ──────────────────────────────────────
function StepLabel({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white text-xs font-bold shrink-0">{n}</span>
      <p className="text-sm font-semibold text-foreground">{label}</p>
    </div>
  );
}

// ─────────────────────────── LocationCard ────────────────────────────────────
function LocationCard({ loc, selected, onClick }: { loc: LocationCardData; selected: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1.5 p-3 sm:p-4 rounded-2xl border-2 transition-all duration-200 w-full
        ${selected
          ? "border-green-500 bg-green-50 dark:bg-green-950/30 shadow-md scale-[1.02]"
          : "border-border bg-card hover:border-green-300 hover:bg-green-50/40 dark:hover:bg-green-950/10 hover:scale-[1.01]"
        }`}>
      <span className="text-xl sm:text-2xl">{loc.emoji}</span>
      <span className={`text-xs sm:text-sm font-semibold ${selected ? "text-green-700 dark:text-green-400" : "text-foreground"}`}>
        {loc.label}
      </span>
      {selected && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
    </button>
  );
}

// ─────────────────────────── LoanTypeCard ────────────────────────────────────
function LoanTypeCard({ product, selected, onClick }: { product: string; selected: boolean; onClick: () => void }) {
  const icon = LOAN_TYPE_ICONS[product] || "📋";
  return (
    <button onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 rounded-2xl border-2 transition-all duration-200 w-full min-h-[72px] sm:min-h-[80px]
        ${selected
          ? "border-green-500 bg-green-50 dark:bg-green-950/30 shadow-md scale-[1.02]"
          : "border-border bg-card hover:border-green-300 hover:bg-green-50/40 dark:hover:bg-green-950/10 hover:scale-[1.01]"
        }`}>
      <span className="text-lg sm:text-xl">{icon}</span>
      <span className={`text-[10px] sm:text-xs font-semibold text-center leading-tight ${selected ? "text-green-700 dark:text-green-400" : "text-foreground"}`}>
        {product}
      </span>
    </button>
  );
}

// ─────────────────────────── LenderCard ──────────────────────────────────────
function LenderCard({ lender, loanType, selected, isFavorite, onToggleFavorite, onClick, bankLogoUrls }: {
  lender: string; loanType: string; selected: boolean;
  isFavorite: boolean; onToggleFavorite: (e: MouseEvent) => void; onClick: () => void;
  bankLogoUrls?: BankLogoMap;
}) {
  return (
    <div className="relative w-full">
      <button onClick={onClick}
        className={`relative flex flex-col items-center justify-center gap-1.5 p-2.5 sm:p-3 rounded-2xl border-2 transition-all duration-200 w-full min-h-[80px] sm:min-h-[90px] pr-5
          ${selected
            ? "border-green-500 bg-green-50 dark:bg-green-950/30 shadow-md ring-2 ring-green-200 dark:ring-green-800"
            : "border-border bg-card hover:border-green-300 hover:bg-green-50/40 dark:hover:bg-green-950/10"
          }`}>
        <LenderLogo lender={lender} loanType={loanType} size="sm" bankLogoUrls={bankLogoUrls} />

        <span className={`text-[9px] sm:text-[10px] font-semibold text-center leading-tight line-clamp-2 px-0.5
          ${selected ? "text-green-700 dark:text-green-400" : "text-muted-foreground"}`}>
          {lender}
        </span>
        {selected && <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />}
      </button>

      {/* Star pin — sibling button, not nested inside the card button */}
      <button
        type="button"
        onClick={onToggleFavorite}
        aria-label={isFavorite ? `Unpin ${lender}` : `Pin ${lender}`}
        className={`absolute top-1 right-1 z-10 p-0.5 rounded-full transition-colors
          ${isFavorite ? "text-yellow-400" : "text-muted-foreground/30 hover:text-yellow-300"}`}
      >
        <Star className="h-3 w-3" fill={isFavorite ? "currentColor" : "none"} />
      </button>
    </div>
  );
}

// ─────────────────────────── Breadcrumb ──────────────────────────────────────
// Shows: 📍 Lucknow > Car Loan > Axis Bank  (all clickable, no edit/change buttons)
function Breadcrumb({ city, loanType, lender, onCityClick, onLoanTypeClick, onLenderClick }: {
  city: string; loanType: string; lender?: string;
  onCityClick: () => void; onLoanTypeClick: () => void;
  onLenderClick?: () => void;
}) {
  return (
    <div className="flex items-center gap-1 flex-wrap text-xs sm:text-sm text-muted-foreground mb-3 bg-muted/40 rounded-xl px-3 py-2">
      <MapPin className="h-3.5 w-3.5 text-green-500 shrink-0" />
      <button onClick={onCityClick} className="font-semibold text-green-600 hover:underline truncate max-w-[80px] sm:max-w-none">
        {city === ALL_CITIES ? "All cities" : city}
      </button>
      {loanType && <>
        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        <button onClick={onLoanTypeClick} className="font-semibold text-green-600 hover:underline truncate max-w-[120px] sm:max-w-none">{loanType}</button>
      </>}
      {lender && <>
        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        <button onClick={onLenderClick} className="font-semibold text-green-600 hover:underline truncate max-w-[120px] sm:max-w-none">{lender}</button>
      </>}
    </div>
  );
}

// ─────────────────────────── SubTabs (Product / Sales Notes / Files) ──────────
interface SubTabsProps {
  selectedLender: string;
  selectedLoanType: string;
  selectedCity: string;
  systemNote: SystemProductNote | null;
  onUpdateSystem: (updates: {
    productNotes?: string; productNotesHistory?: ChangeRecord[];
    smNotes?: string; smNotesHistory?: ChangeRecord[];
    files?: ProductFile[];
  }) => Promise<void>;
  isLoading: boolean;
  userId: string;
  /** Which tab an AskBot deep link asked for; "product" otherwise. */
  defaultTab?: ProductNotesTab;
}

function SubTabs({ selectedLender, selectedLoanType, selectedCity, systemNote, onUpdateSystem, isLoading, userId, defaultTab }: SubTabsProps) {
  // Fix: use correct tab values that match the content keys
  const [subTab, setSubTab] = useState<ProductNotesTab>(defaultTab ?? "product");

  // Product Notes, Sales Notes + Files are all shared (system-wide) content,
  // authored from the loanlens-dashboard admin tool, sourced from systemNote.
  const remoteFiles = systemNote?.files || [];
  const [optimisticFiles, setOptimisticFiles] = useState<typeof remoteFiles>(remoteFiles);
  const remoteFileKey = remoteFiles.map(f => f.id).sort().join(",");
  useEffect(() => { setOptimisticFiles(remoteFiles); }, [remoteFileKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!selectedLender || !selectedLoanType || !selectedCity) {
    return <p className="text-center py-10 text-sm text-muted-foreground">Select a lender to view/edit notes</p>;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">Loading notes...</span>
      </div>
    );
  }

  // Product Notes and Sales Notes are both shared/system content, authored
  // from the loanlens-dashboard admin tool — read-only here. customerNotes is
  // a separate field used for customer-facing sharing and must not be swapped
  // in for either of these.
  const productNotes         = systemNote?.productNotes         || "";
  const productNotesHtml     = systemNote?.productNotesHtml     || "";
  const productNotesFormat   = systemNote?.productNotesFormat   || "richtext";
  const productNotesHistory  = systemNote?.productNotesHistory  || [];
  const smNotes              = systemNote?.smNotes              || "";
  const smNotesHtml          = systemNote?.smNotesHtml          || "";
  const smNotesFormat        = systemNote?.smNotesFormat        || "richtext";
  const smNotesHistory       = systemNote?.smNotesHistory       || [];

  const TAB_CONFIG = [
    { val: "product" as const, icon: <Package    className="h-3.5 w-3.5" />, label: "Product Notes",     short: "Product Notes"  },
    { val: "sales"   as const, icon: <Users      className="h-3.5 w-3.5" />, label: "Sales Notes", short: "Sales Notes"    },
    { val: "files"   as const, icon: <FolderOpen className="h-3.5 w-3.5" />, label: "Files",       short: "Files"    },
  ];

  return (
    <Tabs value={subTab} onValueChange={v => setSubTab(v as typeof subTab)} className="w-full">
      <TabsList className="grid w-full grid-cols-3 h-9 bg-muted/60 rounded-lg p-0.5">
        {TAB_CONFIG.map(t => (
          <TabsTrigger key={t.val} value={t.val}
            className="text-xs data-[state=active]:bg-green-500 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md flex items-center justify-center gap-1 px-1">
            {t.icon}
            <span className="hidden sm:inline">{t.label}</span>
            <span className="sm:hidden">{t.short}</span>
          </TabsTrigger>
        ))}
      </TabsList>

      {/* Product Notes — shared system content, admin-authored, read-only here */}
      <TabsContent value="product" className="mt-3">
        <ProductNotesCard title="Product Notes" notes={productNotes} html={productNotesHtml} format={productNotesFormat}
          onSave={() => {}}
          changeHistory={productNotesHistory} disabled userId={userId} />
      </TabsContent>

      {/* Sales Notes — shared system content, admin-authored, read-only here (maps to smNotes not customerNotes) */}
      <TabsContent value="sales" className="mt-3">
        <ProductNotesCard title="Sales Notes" notes={smNotes} html={smNotesHtml} format={smNotesFormat}
          onSave={() => {}}
          changeHistory={smNotesHistory} showWhatsAppShare disabled userId={userId} />
      </TabsContent>

      {/* Files — shared system reference files */}
      <TabsContent value="files" className="mt-3">
        <ProductFilesSection
          files={optimisticFiles}
          onFilesChange={updatedFiles => { setOptimisticFiles(updatedFiles); onUpdateSystem({ files: updatedFiles }); }}
          disabled={false} userId={userId} isSystem={true}
          bank={selectedLender} loanType={selectedLoanType} />
      </TabsContent>
    </Tabs>
  );
}

// ─────────────────────────── ManualMatcher (main) ────────────────────────────
export function ManualMatcher() {
  const { config, loading: configLoading } = useAppConfig();
  const { user } = useAuth();

  const [selectedCity,     setSelectedCity]     = useState("");
  const [selectedLoanType, setSelectedLoanType] = useState("");
  const [selectedLender,   setSelectedLender]   = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  // Latches once an AskBot deep link has seeded the cascade.
  const seededFromLinkRef = useRef(false);
  const [linkTab, setLinkTab] = useState<ProductNotesTab | undefined>(undefined);

  const [locationSearch, setLocationSearch] = useState("");
  const [loanSearch,     setLoanSearch]     = useState("");
  const [lenderSearch,   setLenderSearch]   = useState("");

  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try { const s = localStorage.getItem("lender-favorites"); return s ? new Set(JSON.parse(s)) : new Set(); }
    catch { return new Set(); }
  });

  const toggleFavorite = useCallback((lender: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = new Set(prev);
      next.has(lender) ? next.delete(lender) : next.add(lender);
      try { localStorage.setItem("lender-favorites", JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  const [systemNote,      setSystemNote]      = useState<SystemProductNote | null>(null);
  const [systemLoading,   setSystemLoading]   = useState(false);

  const effectiveCity = selectedCity === "__all__" ? "" : selectedCity;

  // Product Notes, Sales Notes + Files are all shared/system-wide — same
  // Firestore doc the loanlens-dashboard admin tool writes to (product_notes_system).
  useEffect(() => {
    if (!selectedLender || !selectedLoanType || !selectedCity) { setSystemNote(null); return; }
    setSystemLoading(true);
    const onNote = (note: SystemProductNote | null) => { setSystemNote(note); setSystemLoading(false); };
    // "All cities" is not a city to filter on — it means "whichever record
    // exists for this bank", the same resolution the chatbot does when it has
    // no city. Picking a real city keeps the exact-match behaviour.
    return selectedCity === ALL_CITIES
      ? subscribeToSystemNotesAnyCity(selectedLender, selectedLoanType, onNote)
      : subscribeToSystemNotes(selectedLender, selectedLoanType, onNote, effectiveCity || undefined);
  }, [selectedLender, selectedLoanType, selectedCity, effectiveCity]);

  const handleSystemUpdate = useCallback(async (updates: { productNotes?: string; productNotesHistory?: ChangeRecord[]; smNotes?: string; smNotesHistory?: ChangeRecord[]; files?: ProductFile[] }) => {
    if (!selectedLender || !selectedLoanType || !selectedCity || !user?.userId) return;
    // Write back to the city of the record actually on screen. Under "All
    // cities" that may be a city-specific record, and passing the empty city
    // would silently create a second, national record instead of updating it.
    const writeCity = systemNote?.city ?? effectiveCity;
    try { await updateSystemNotes(selectedLender, selectedLoanType, updates, user.userId, writeCity || undefined); }
    catch { toast.error("Failed to save changes. Please try again."); }
  }, [selectedLender, selectedLoanType, selectedCity, user?.userId, effectiveCity, systemNote?.city]);

  const hasError = !configLoading && (!config.banks.length || !config.products.length);

  // ── Navigation helpers ──
  const goToStep1 = useCallback(() => {
    setSelectedCity(""); setSelectedLoanType(""); setSelectedLender("");
    setLocationSearch(""); setLoanSearch(""); setLenderSearch("");
  }, []);
  const goToStep2 = useCallback(() => {
    setSelectedLoanType(""); setSelectedLender("");
    setLoanSearch(""); setLenderSearch("");
  }, []);
  const goToStep3 = useCallback(() => {
    setSelectedLender(""); setLenderSearch("");
  }, []);

  // Only show locations that actually appear in bankLoanTypeLocations, not the
  // full admin-curated city list — cross-referencing card metadata (emoji) for
  // locations that also happen to be in that curated list.
  const mappedLocationCards = useMemo(() => {
    const mapped = getAllLocations(config.bankLoanTypeLocations);
    const byLabel = new Map(config.locationCards.map(c => [c.label.toLowerCase(), c]));
    return mapped.map((loc) => {
      const card = byLabel.get(loc.toLowerCase());
      return {
        key: loc,
        label: card?.label ?? loc,
        emoji: card?.emoji ?? "📍",
      };
    });
  }, [config.bankLoanTypeLocations, config.locationCards]);

  const filteredLocations = useMemo(() =>
    mappedLocationCards.filter(l => l.label.toLowerCase().includes(locationSearch.toLowerCase()))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [mappedLocationCards, locationSearch]);

  // ── AskBot deep link ────────────────────────────────────────────────────────
  // Links from the chatbot arrive as ?city=&loanType=&lender=&tab=. Seed the
  // cascade one step at a time and stop at the first value that matches
  // nothing, so a stale link degrades into the normal picker at that step
  // rather than a blank screen.
  //
  // The product track resolves a bank and a loan type but never a city, so
  // `city` is usually the ALL_CITIES sentinel — accepted as-is, since it is
  // not a value the location list contains.
  //
  // Runs once, and clears the params afterwards, so that walking back up the
  // breadcrumb isn't immediately undone by a re-seed.
  useEffect(() => {
    if (seededFromLinkRef.current || configLoading || mappedLocationCards.length === 0) return;

    const wantCity     = searchParams.get("city");
    const wantLoanType = searchParams.get("loanType");
    const wantLender   = searchParams.get("lender");
    const wantTab      = searchParams.get("tab");
    if (!wantCity && !wantLoanType && !wantLender) return;

    seededFromLinkRef.current = true;

    const norm = (s: string) => s.trim().toLowerCase();
    let city: string | undefined;
    if (wantCity === ALL_CITIES) {
      city = ALL_CITIES;
    } else if (wantCity) {
      // Match on either spelling but store the card's `key`, which is what the
      // card click handler sets and what the cascade helpers expect.
      city = mappedLocationCards.find(
        (c) => norm(c.key) === norm(wantCity) || norm(c.label) === norm(wantCity),
      )?.key;
    }

    const loanTypes = city
      ? city === ALL_CITIES
        ? [...new Set(
            Object.values(config.bankLoanTypeLocations || {})
              .flatMap((lt) => Object.keys(lt || {})),
          )]
        : getLoanTypesForLocation(config.bankLoanTypeLocations, city)
      : [];
    const loanType = matchLoanTypeOption(loanTypes, wantLoanType);

    const lenders = city && loanType
      ? city === ALL_CITIES
        ? Object.entries(config.bankLoanTypeLocations || {})
            .filter(([, lt]) => !!lt?.[loanType])
            .map(([lender]) => lender)
        : getLendersForLocationLoanType(config.bankLoanTypeLocations, city, loanType)
      : [];
    const lender = matchOption(lenders, wantLender);

    if (city)     setSelectedCity(city);
    if (loanType) setSelectedLoanType(loanType);
    if (lender)   setSelectedLender(lender);
    if (wantTab === "product" || wantTab === "sales" || wantTab === "files") {
      setLinkTab(wantTab);
    }

    setSearchParams({}, { replace: true });
  }, [configLoading, config.bankLoanTypeLocations, mappedLocationCards, searchParams, setSearchParams]);

  // Loan types available for the selected location, derived from
  // appConfig/main.bankLoanTypeLocations (same cascade Payouts & DSA Code uses).
  // Under "All cities" the location cascade has nothing to filter on, so fall
  // back to every loan type any lender offers anywhere.
  const availableLoanTypes = useMemo(() => {
    if (!selectedCity) return [];
    if (selectedCity !== ALL_CITIES) {
      return getLoanTypesForLocation(config.bankLoanTypeLocations, selectedCity);
    }
    const set = new Set<string>();
    for (const loanTypes of Object.values(config.bankLoanTypeLocations || {})) {
      for (const loanType of Object.keys(loanTypes || {})) set.add(loanType);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [config.bankLoanTypeLocations, selectedCity]);

  const filteredLoanTypes = useMemo(() =>
    [...availableLoanTypes]
      .filter(p => p.toLowerCase().includes(loanSearch.toLowerCase()))
      .sort((a, b) => a.localeCompare(b)),
    [availableLoanTypes, loanSearch]);

  // Lenders that offer the selected loan type in the selected location.
  const availableLenders = useMemo(() => {
    if (!selectedCity || !selectedLoanType) return [];
    if (selectedCity !== ALL_CITIES) {
      return getLendersForLocationLoanType(config.bankLoanTypeLocations, selectedCity, selectedLoanType);
    }
    return Object.entries(config.bankLoanTypeLocations || {})
      .filter(([, loanTypes]) => !!loanTypes?.[selectedLoanType])
      .map(([lender]) => lender)
      .sort((a, b) => a.localeCompare(b));
  }, [config.bankLoanTypeLocations, selectedCity, selectedLoanType]);

  const filteredLenders = useMemo(() => {
    const filtered = availableLenders.filter(b =>
      b.toLowerCase().includes(lenderSearch.toLowerCase()) &&
      b.trim().toLowerCase() !== "other"
    );
    const favs = filtered.filter(b => favorites.has(b)).sort((a, b) => a.localeCompare(b));
    const rest  = filtered.filter(b => !favorites.has(b)).sort((a, b) => a.localeCompare(b));
    return [...favs, ...rest];
  }, [availableLenders, lenderSearch, favorites]);

  const step = !selectedCity ? 1 : !selectedLoanType ? 2 : !selectedLender ? 3 : 4;

  return (
    <>
      <div className="mm-root space-y-4 w-full">

        {configLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
          </div>
        )}

        {hasError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Failed to load banks and products from Firebase.</AlertDescription>
          </Alert>
        )}

        {!configLoading && !hasError && (
          <>
            {/* Breadcrumb — shows city > loanType > lender (all clickable, NO change/edit buttons) */}
            {selectedCity && (
              <Breadcrumb
                city={selectedCity}
                loanType={selectedLoanType}
                lender={step === 4 ? selectedLender : undefined}
                onCityClick={goToStep1}
                onLoanTypeClick={goToStep2}
                onLenderClick={goToStep3}
              />
            )}

            {/* ── STEP 1 : Location ── */}
            {step === 1 && (
              <div>
                <StepLabel n={1} label="Select your city" />
                <SearchInput value={locationSearch} onChange={setLocationSearch} placeholder="Search for Location" />
                <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3">
                  {filteredLocations.map(loc => (
                    <LocationCard key={loc.key} loc={loc} selected={selectedCity === loc.key}
                      onClick={() => { setSelectedCity(loc.key); setSelectedLoanType(""); setSelectedLender(""); }} />
                  ))}
                  {filteredLocations.length === 0 && (
                    <p className="col-span-full text-center text-sm text-muted-foreground py-6">No locations found</p>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 2 : Loan Type ── */}
            {step === 2 && (
              <div>
                <StepLabel n={2} label="Select loan type" />
                <SearchInput value={loanSearch} onChange={setLoanSearch} placeholder="Search for Loan Type" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                  {filteredLoanTypes.map(product => (
                    <LoanTypeCard key={product} product={product} selected={selectedLoanType === product}
                      onClick={() => { setSelectedLoanType(product); setSelectedLender(""); }} />
                  ))}
                  {filteredLoanTypes.length === 0 && (
                    <p className="col-span-full text-center text-sm text-muted-foreground py-6">No loan types found</p>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 3 : Lender ── */}
            {step === 3 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-white text-xs font-bold shrink-0">3</span>
                  <p className="text-sm font-semibold text-foreground">Select lender</p>
                  {favorites.size > 0 && (
                    <span className="ml-auto flex items-center gap-1 text-[10px] sm:text-xs text-yellow-500 font-medium whitespace-nowrap">
                      <Star className="h-3 w-3 fill-yellow-400" />
                      {favorites.size} pinned
                    </span>
                  )}
                </div>
                <SearchInput value={lenderSearch} onChange={setLenderSearch} placeholder="Search for Lender" />
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
                  {filteredLenders.map(bank => (
                    <LenderCard key={bank} lender={bank} loanType={selectedLoanType}
                      selected={selectedLender === bank}
                      isFavorite={favorites.has(bank)}
                      onToggleFavorite={e => toggleFavorite(bank, e)}
                      onClick={() => setSelectedLender(bank)}
                      bankLogoUrls={config.bankLogoUrls} />
                  ))}
                  {filteredLenders.length === 0 && (
                    <p className="col-span-full text-center text-sm text-muted-foreground py-6">No lenders found</p>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 4 : Notes & Files ── */}
            {step === 4 && (
              <SubTabs
                selectedLender={selectedLender}
                selectedLoanType={selectedLoanType}
                selectedCity={selectedCity}
                systemNote={systemNote}
                onUpdateSystem={handleSystemUpdate}
                isLoading={systemLoading}
                userId={user?.userId || "unknown"}
                defaultTab={linkTab}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
